import type { AfterNavigateEvent, AfterSubmitEvent, IframeGoogleAnalyticsSubmissionEventEventData, IframeGoogleAnalyticsSubmissionRecordedEvent, InitGoogleAnalyticsEventData } from 'src/framework/public/events';
import { FormEvents } from 'src/framework/public/events';
import Vue from 'vue';
import { GoogleAnalyticsApi } from './ga-api';
import { FormEvent } from 'src/framework/eventing/form-event';

export default Vue.extend({
	name: 'CGoogleAnalytics',
	inject: ['flags', 'formEvents', 'auditService'],
	props: {
		trackingId: { type: String, default: '', required: true },
		formInternalName: { type: String, default: '' },
		trackFormSubmission: { type: Boolean, default: false },
		submissionGoalLabel: { type: String, default: '' },
		submissionGoalValue: { type: Number, default: null },
		isMultiPage: { type: Boolean, default: false },
		firstVisiblePageName: { type: String, default: null }
	},
	data() {
		return {
			googleAnalytics: null
		};
	},
	mounted() {
		if (!this.flags.iframeFormTracking) {
			this.googleAnalytics = new GoogleAnalyticsApi(this.trackingId, this.formInternalName, this.flags.embedded);

			// Record the initial page view
			this.googleAnalytics.recordPageView(this.isMultiPage ? this.firstVisiblePageName : null);

			this.formEvents.on(FormEvents.AfterNavigate, this.recordNavigation);
			this.formEvents.on(FormEvents.AfterSubmit, this.recordSubmission);
			this.formEvents.on(FormEvents.AfterSubmit, this.recordConfirmation);
		}
		else {
			this.formEvents.emit(new FormEvent<InitGoogleAnalyticsEventData>(FormEvents.InitGoogleAnalytics, {
				googleAnalyticsTrackingId: this.trackingId,
				formInternalName: this.formInternalName,
				isMultipageForm: this.isMultiPage,
				firstVisiblePageName: this.firstVisiblePageName
			}));
			this.formEvents.on(FormEvents.AfterSubmit, this.recordIframeSubmission);
		}
	},
	methods: {
		recordNavigation(event: AfterNavigateEvent) : Promise<boolean> {
			return this.googleAnalytics.recordNavigation(event);
		},
		recordConfirmation(): Promise<boolean> {
			// Record the confirmation page after the form is submitted
			return this.googleAnalytics.recordConfirmation();
		},
		async recordSubmission(event: AfterSubmitEvent) {
			if (this.trackFormSubmission) {
				const result = await this.googleAnalytics.recordSubmission(this.submissionGoalLabel, this.submissionGoalValue);
				const entryId = event.data.entry.Id;
				const recordId = event.data.auditRecordId;
				this.recordGoalAuditRecord(this.submissionGoalLabel, this.submissionGoalValue, result, entryId, recordId);
			}
		},
		recordIframeSubmission(submitEvent: AfterSubmitEvent) {
			const entryId = submitEvent.data.entry.Id;
			const recordId = submitEvent.data.auditRecordId;
			if (this.trackFormSubmission) {
				this.formEvents.once(FormEvents.IframeGoogleAnalyticsSubmissionRecordedEvent,
					(recordedEvent: IframeGoogleAnalyticsSubmissionRecordedEvent) =>
						this.recordGoalAuditRecordIframe(recordedEvent, entryId, recordId));
				this.formEvents.emit(new FormEvent<IframeGoogleAnalyticsSubmissionEventEventData>(FormEvents.IframeGoogleAnalyticsSubmissionEvent, {
					goalLabel: this.submissionGoalLabel,
					goalValue: this.submissionGoalValue
				}));
			}
		},
		async recordGoalAuditRecord(label: string, value: number, successful: boolean, entryId: string, auditRecordId: string) {
			await this.auditService.recordGoogleAnalyticsOutcome(this.formInternalName, GoogleAnalyticsApi.SubmissionAction, label, value, successful, entryId, auditRecordId);
		},
		recordGoalAuditRecordIframe(event: IframeGoogleAnalyticsSubmissionRecordedEvent, entryId: string, auditRecordId: string) {
			this.recordGoalAuditRecord(event.data.goalLabel, event.data.goalValue, event.data.successful, entryId, auditRecordId);
		}
	},
	render() {
		return null;
	}
});